import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Connector from '../../Components/Connector';
import Page from '../../Components/Page';
import LoadingAnimation from '../../Components/Utils/BateryChargeAnimation';
import {
  setChargingPoints,
  updateOCPPChargingPoints
} from '../../redux/reducers/chargingPointsSlice';

import TabBar from '../../Components/Utils/TabBar/index.jsx';
import Map from '../../Components/GoogleMap/index.jsx';
import IfRender from '../../Components/Utils/IfRender';

import { selectChargingPoints } from '../../redux/selectors/chargingPoints';
import { selectAuthData } from '../../redux/selectors/auth';
import { sortByStatus, unwindConnectors } from '../../Utils/connectors';
import { getNrOfColumns, makeMasonryMatrix } from '../../Utils/masonry';
import { chargePointsAndConnectorsFilter } from '../../QueryFilters/queries.js';
import { selectUserType } from '../../redux/selectors/userType.js';

import './style.scss';
import useWebSocket from '../../Utils/useWebsocket.js';
import { useLazyRequestChargingPointsQuery } from '../../redux/api/chargingPoints.js';

export default function Dashboard() {
  const contentRef = useRef();
  const dispatch = useDispatch();
  const [_, setColumnCount] = useState(null);
  const { user: { id: userId = '' } = {} } = useSelector(selectAuthData);
  const chargingPoints = useSelector(selectChargingPoints);
  const [requestChargingPoints, chargingPointsData] = useLazyRequestChargingPointsQuery();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const webSocketsRef = useRef([]);

  const fetchData = () => requestChargingPoints(JSON.stringify(chargePointsAndConnectorsFilter));

  useEffect(() => {
    if (chargingPointsData.isSuccess) {
      dispatch(setChargingPoints({ list: chargingPointsData.data }));
    }
  }, [chargingPointsData.isSuccess]);

  const connectors = useMemo(() => unwindConnectors(chargingPoints), [chargingPoints]);

  const chargePointsIds = chargingPointsData.data
    ? chargingPointsData.data.map((item) => item.id)
    : '';

  useWebSocket(
    useMemo(
      () => [
        {
          nameSpace: 'chargers',
          data: userId,
          responseEvent: 'dashboardPageUpdates',
          additionalEvents: []
        }
      ],
      [userId]
    ),
    useMemo(() => ({ dashboardPageUpdates: updateOCPPChargingPoints }), []),
    chargePointsIds,
    webSocketsRef
  );

  const sortAndRenderConnectors = () => {
    if (contentRef.current && chargingPointsData.data) {
      const sortedConnectors = sortByStatus(connectors);
      const matrix = makeMasonryMatrix(sortedConnectors, contentRef.current);
      return matrix.map((column, index) => (
        <div key={`column-${index}`} className='connector__column'>
          {column.map((connector, i) => (
            <Connector key={`connector-${i}`} connector={connector} />
          ))}
        </div>
      ));
    }
    return null;
  };

  const onResize = () => {
    setColumnCount(getNrOfColumns(contentRef.current));
  };

  useEffect(() => {
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const tabIcons = [
    { icon: 'dashboard', fnc: setActiveTab },
    { icon: 'map', fnc: setActiveTab }
  ];

  const headerItems = (
    <TabBar
      tabs={tabIcons.map((btn) => ({ iconName: btn.icon, onClick: btn.fnc }))}
      activeTabIndex={activeTab}
    />
  );

  useEffect(() => {
    fetchData();
  }, []);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return (
    <Page
      title={t('dashboard')}
      pageClass='page__100vh'
      contentClass='dashboard'
      contentRef={contentRef}
      customHeaderItems={headerItems}>
      {activeTab === 0 && (
        <IfRender condition={chargingPoints?.length} otherwise={<LoadingAnimation />}>
          {sortAndRenderConnectors()}
        </IfRender>
      )}
      {activeTab === 1 && <Map chargingPoints={chargingPoints} />}
    </Page>
  );
}
